import { Navigate, Outlet } from 'react-router-dom';
import type { Permissions, ROLE_TYPES } from '@/Interfaces/Data';
import type { DotNestedKeys } from '@/_types/utils';
import useCheckPermissions from '@/hooks/util/useCheckPermissions';
import { useAppSelector } from '@/_store/hooks';
import { selectUser } from '@/_store/modules/auth/selectors';

interface AuthorizationOutletProps {
  permissions: ROLE_TYPES[];
  permissionKeys?: DotNestedKeys<Permissions>[];
  redirectTo?: string;
}

const PERMISSION_DENIED = false;
const PERMISSION_GRANTED = true;

/**
 * Renders an outlet component if the user has the required permission.
 * Otherwise, redirects to the specified route.
 * @param permissions - The required permissions to access the component.
 * @param permissionKeys - User permissions that also grant access, regardless of the role.
 * @param redirectTo - The route to redirect to if the user does not have permission.
 * @returns The outlet component or a redirect component.
 */
function AuthorizationOutlet({
  permissions,
  permissionKeys = [],
  redirectTo = '/',
}: AuthorizationOutletProps) {
  const user = useAppSelector(selectUser);
  const hasPermissionKey = useCheckPermissions(...permissionKeys);

  const hasPermission = (() => {
    if (!user?.managerLogType) {
      return PERMISSION_DENIED;
    }

    if (permissions.includes(user?.managerLogType)) {
      return PERMISSION_GRANTED;
    }

    if (hasPermissionKey) {
      return PERMISSION_GRANTED;
    }

    return PERMISSION_DENIED;
  })();

  if (!hasPermission) {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}

export { AuthorizationOutlet };
